import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { generatePath, useParams } from 'react-router'



import { useBlogPost, useRelatedBlogPosts } from '@entities/Blog'


import { BlogDetail } from '@widgets/blog-detail'
import { BlogPreview } from '@widgets/blog-preview'
import NotFoundComponent from '@widgets/not-found/NotFoundComponent'



import SEO from '@shared/components/SEO'
import { URL_BLOG, URL_BLOG_CATEGORY } from '@shared/contants'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import Wrapper from '@shared/ui/wrapper'

import styles from './styles.module.scss'

function BlogDetailSkeleton () {
  return (
    <Container>
      <PageGrid>
        <Wrapper className={styles.wrapper}>
          <SkeletonTheme>
            {/* Breadcrumbs skeleton */}
            <div className={styles.breadcrumbs}>
              <Skeleton width={200} height={20} style={{ marginBottom: 8 }} />
            </div>

            {/* Article skeleton */}
            <div className={styles.articleSkeleton}>
              {/* Title */}
              <Skeleton width="80%" height={40} style={{ marginBottom: 16 }} />

              {/* Meta info */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: 24 }}>
                <Skeleton width={80} height={24} style={{ borderRadius: 12 }} />
                <Skeleton width={100} height={24} />
                <Skeleton width={60} height={24} />
              </div>

              {/* Main image */}
              <Skeleton
                width="100%"
                height={300}
                style={{ borderRadius: 12, marginBottom: 24 }}
              />

              {/* Lead text */}
              <Skeleton width="100%" height={20} style={{ marginBottom: 8 }} />
              <Skeleton width="85%" height={20} style={{ marginBottom: 24 }} />

              {/* Content blocks */}
              <Skeleton width="100%" height={20} style={{ marginBottom: 12 }} />
              <Skeleton width="95%" height={20} style={{ marginBottom: 12 }} />
              <Skeleton width="90%" height={20} style={{ marginBottom: 24 }} />

              <Skeleton width="70%" height={20} style={{ marginBottom: 12 }} />
              <Skeleton width="100%" height={20} style={{ marginBottom: 12 }} />
              <Skeleton width="80%" height={20} style={{ marginBottom: 24 }} />

              {/* Share button */}
              <Skeleton width={150} height={48} style={{ borderRadius: 8 }} />
            </div>
          </SkeletonTheme>
        </Wrapper>
      </PageGrid>
    </Container>
  )
}

export default function BlogDetailPage () {
  const { slug } = useParams<{ slug: string }>()

  const { t: tb } = useTranslation('blog')
  // const isTablet = useMediaQuery("(max-width: 1279px)");

  const { data, loading } = useBlogPost({
    slug,
  })

  const { data: relatedPosts, loading: loadingRelated } = useRelatedBlogPosts({
    categorySlug: data?.category?.slug,
    excludeId: data?.id,
    themeSlug: data?.themes?.[0]?.slug,
  })

  const { data: themePosts, loading: loadingThemePosts } = useRelatedBlogPosts({
    themeSlug: data?.themes?.[0]?.slug,
    excludeId: data?.id,
  })

  if (loading) {
    return <BlogDetailSkeleton />
  }

  if (!data) {
    return (
      <Container>
        <PageGrid>
          <Wrapper>
            <NotFoundComponent />
          </Wrapper>
        </PageGrid>
      </Container>
    )
  }

  const content = (
    <Wrapper className={styles.wrapper}>
      <SEO
        title={data?.title || tb('blog')}
        description={data?.lead || tb('blog')}
        ogImage={data?.preview?.url}
      />
      {data.type?.id !== '2' && (
        <Breadcrumbs
          className={styles.breadcrumbs}
          links={[
            {
              title: tb('blog'),
              to: URL_BLOG,
            },
            ...(data.category?.slug
              ? [
                {
                  title: data.category?.title as string,
                  to: generatePath(URL_BLOG_CATEGORY, {
                    categorySlug: data.category?.slug,
                  }),
                },
              ]
              : []),
            {
              title: data?.title as string,
            },
          ]}
        />
      )}
      <BlogDetail data={data} />
      {relatedPosts.length > 0 && (
        <BlogPreview
          title={tb('related.articles')}
          articles={relatedPosts || []}
          loading={loadingRelated}
          useBigSize={false}
          isCarousel
        />
      )}
      {data.themes?.[0] && themePosts.length > 0 && (
        <BlogPreview
          title={data.themes[0].title}
          articles={themePosts || []}
          loading={loadingThemePosts}
          useBigSize={false}
          isCarousel
        />
      )}
    </Wrapper>
  )

  if (data.type?.id === '2') {
    return (
      <Container>
        {content}
      </Container>
    )
  }

  return (
    <Container>
      <PageGrid>
        {content}
      </PageGrid>
    </Container>
  )
}
